import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchInput } from '@/components/books'
import { BookThumb } from '@/components/insights'
import { Card, CardContent } from '@/components/ui'
import { useLibraryBooks } from '@/hooks/insights'

/** Every note the user wrote on their books, searchable in one place */
export function NotesPage() {
  const { t } = useTranslation()
  const { data: books = [], isLoading } = useLibraryBooks()
  const [search, setSearch] = useState('')

  const withNotes = useMemo(
    () =>
      books.filter((b) => b.notes?.trim()).sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [books]
  )

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase()
    if (!query) return withNotes
    return withNotes.filter((b) =>
      [b.title, b.author, b.notes ?? ''].some((field) => field.toLocaleLowerCase().includes(query))
    )
  }, [withNotes, search])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">{t('insights.notes.title')}</h1>
        <p className="mt-1 text-muted-foreground">
          {isLoading
            ? t('common.loading')
            : t('insights.notes.subtitle', { count: withNotes.length })}
        </p>
      </div>

      {withNotes.length > 0 && (
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('insights.notes.searchPlaceholder')}
        />
      )}

      {!isLoading && withNotes.length === 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center text-muted-foreground">
            <div className="mb-4 text-4xl">📝</div>
            <p>{t('insights.notes.empty')}</p>
          </CardContent>
        </Card>
      )}

      {withNotes.length > 0 && filtered.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">{t('common.noResultsMessage')}</p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {filtered.map((book) => (
          <Card key={book.id} className="border-0 shadow-sm min-w-0">
            <CardContent className="p-5">
              <div className="mb-3 flex items-center gap-3">
                <BookThumb src={book.cover_url} title={book.title} />
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">{book.title}</p>
                  <p className="truncate text-sm text-muted-foreground">{book.author}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(`books.status.${book.status}`)}
                  </p>
                </div>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm text-foreground">
                {book.notes}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
